import React, { useState } from "react";
import {
  Typography,
  Box,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Grid2,
} from "@mui/material";

import { useGetActiveRiskSeverityQuery } from "../../../store/slices/apis/riskDashboardSlice";
import { useParams } from "react-router-dom";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { FaSquareFull } from "react-icons/fa6";
import RiskPopup from "./RiskPopup";

const ActiveRiskGraph = () => {
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { data: ActiveRiskSeverity = [], isLoading } =
    useGetActiveRiskSeverityQuery({
      project_id: projectId,
    });

  const totalCount =
    ActiveRiskSeverity?.data?.reduce((sum, item) => sum + item.count, 0) || 0;

  const moduleColorMap = {
    Low: "#3E756D",
    Moderate: "#FFBF00",
    High: "#FF7E5C",
    Critical: "#EC503F",
  };

  const severityData = ActiveRiskSeverity?.data?.map((item) => ({
    y: item.count,
    value: item.count,
    color: moduleColorMap[item.severity],
    name: item.severity,
    percentage: (item.count / totalCount) * 100,
  }));

  const options = {
    chart: {
      type: "pie",
      width: 220,
      height: 170,
      marginBottom: 0,
      marginTop: 0,
    },
    title: { text: "" },
    credits: { enabled: false },
    tooltip: {
      formatter: function () {
        return `<b style='color: ${this.color}'>${this.name}</b>: ${this.y}`;
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        borderWidth: 0,
        borderRadius: 0,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: false,
        size: "90%",
        innerSize: "60%",
        point: {
          events: {
            click: function () {
              setPopupData({
                name: this.name,
                value: this.y,
              });
              setOpen(true);
            },
          },
        },
      },
    },
    series: [
      {
        name: "",
        dataLabels: {
          verticalAlign: "top",
          enabled: true,
          style: {
            fontWeight: 600,
            fontSize: "9px",
            color: "#FFFFFF",
            lineHeight: "40px",
            textOutline: "none",
          },
          distance: -15,
          formatter: function () {
            return Math.round(this.percentage) + "%";
          },
        },
        data: severityData,
      },
    ],
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          backgroundColor: "white",
          marginTop: "10px",
          borderRadius: "5px",
          padding: "20px",
        }}
      >
        <Skeleton variant="text" width={220} height={28} />
        <Skeleton variant="text" width={220} height={80} />
        <Grid2 container>
          <Grid2 xs={12} md={7}>
            {[...Array(3)].map((_, index) => (
              <Grid2
                container
                direction="row"
                spacing={1}
                key={index}
                sx={{ marginLeft: "10px", paddingTop: "5px" }}
              >
                <Box sx={{ width: "25px", padding: "8px" }}>
                  <Skeleton variant="circular" width={18} height={18} />
                </Box>
                <Box sx={{ width: "130px", padding: "8px" }}>
                  <Skeleton variant="text" width={100} height={20} />
                </Box>
                <Box sx={{ width: "30px", padding: "4px" }}>
                  <Skeleton variant="text" width={20} height={28} />
                </Box>
              </Grid2>
            ))}
          </Grid2>
          <Grid2
            xs={12}
            md={5}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Skeleton variant="circular" width={175} height={175} />
          </Grid2>
        </Grid2>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 2,
          maxWidth: "100%",
          width: "100%", // fixed from "100"
          height: { xs: "auto", md: "251px" },
          border: "1px solid #D8D8D8",
          mt: 0,
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: "white",
        }}
      >
        <Box>
          <Typography
            variant="body1" // changed from "body"
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              mb: 0,
              lineHeight: "100%",
              letterSpacing: "-0.4px",
              color: theme?.customSettings?.themeColor,
            }}
          >
            Active Risk by Severity Level
          </Typography>
          <Typography
            sx={{
              mt: "5px",
              color: "#5052588A",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
            }}
          >
            Risk severity to prioritize mitigation efforts and safeguard project objectives.
          </Typography>
        </Box>
        {severityData?.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 6,
            }}
          >
            <Typography
              sx={{ fontSize: "16px", color: "#999", fontWeight: 500 }}
            >
              No data available to display the chart.
            </Typography>
          </Box>
        ) : (
          <Grid2
            container
            spacing={2}
            sx={{ mt: 1 }}
          >
            <Grid2 size={{ xs: 12, md: 6 }}>
              {/* List */}
              <List sx={{ padding: 0, mb: 4 }}>
                {severityData?.map((val, index) => (
                  <ListItem key={index} sx={{ paddingLeft: 0 }}>
                    <ListItemIcon sx={{ minWidth: "20px" }}>
                      <FaSquareFull
                        style={{
                          color: val?.color,
                          height: "14px",
                          width: "14px",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <>
                          <Typography
                            component="span"
                            sx={{
                              whiteSpace: "nowrap",
                              fontWeight: 500,
                              fontSize: "16px",
                              lineHeight: "100%",
                              letterSpacing: "-0.4px",
                            }}
                          >
                            {val?.name}:{" "}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              color: "#182A4E",
                              whiteSpace: "nowrap",
                              fontWeight: 500,
                              fontSize: "16px",
                              lineHeight: "100%",
                              letterSpacing: "-0.4px",
                            }}
                          >
                            {val?.value}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Grid2>
            {/* Graph */}
            <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Box sx={{ flexShrink: 0 }}>
                <HighchartsReact highcharts={Highcharts} options={options} />
              </Box>
            </Grid2>
          </Grid2>
        )}
      </Box>

      {open && (
        <RiskPopup
          title={"Active Risk Severity"}
          value={popupData.name}
          status={"severity"}
          onOpen={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default ActiveRiskGraph;
